import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import type { Dispatch, DragEvent as ReactDragEvent, KeyboardEvent as ReactKeyboardEvent, SetStateAction } from 'react'
import { Paperclip, Send, StopCircle } from 'lucide-react'
import { uploadDocument } from '../lib/api'
import { useI18n } from '../lib/i18n'
import type { AttachedDoc } from '../lib/types'
import {
  AttachmentChip,
  AttachmentErrorChip,
  AttachmentUploadingChip,
} from './AttachmentChip'

export interface ComposerHandle {
  fill: (text: string) => void
}

interface Props {
  onSend: (text: string) => void
  onStop: () => void
  isStreaming: boolean
  disabled?: boolean
  /** Currently attached docs for this conversation, in user-pick order. */
  attachedDocs: AttachedDoc[]
  /** React-style setter so the Composer can append after each successful
   *  upload and remove on ×-click. */
  onAttachmentsChange: Dispatch<SetStateAction<AttachedDoc[]>>
}

// Mirror of pipeline.uploads.SUPPORTED_SUFFIXES — used to set
// `<input accept=…>` and to filter dropped files.
const ACCEPT_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.markdown']
const ACCEPT_ATTR = ACCEPT_EXTENSIONS.join(',')

// Match backend `pipeline.uploads.MAX_UPLOAD_BYTES` (kept in sync
// manually — server still validates definitively).
const MAX_FILE_BYTES = 20 * 1024 * 1024

// UI-side cap on simultaneously-attached documents.  Anything past 5
// makes the chip row visually messy and risks blowing past the LLM's
// context window even with per-doc text truncation.
const MAX_ATTACHMENTS = 5

/**
 * Live state for the in-flight upload (one at a time — we process the
 * picked file batch serially so failures don't cascade).  ``index`` /
 * ``total`` show "N of M" progress while a multi-file batch is running.
 */
interface UploadingState {
  filename: string
  size_bytes: number
  index: number
  total: number
}

export const Composer = forwardRef<ComposerHandle, Props>(function Composer({
  onSend,
  onStop,
  isStreaming,
  disabled,
  attachedDocs,
  onAttachmentsChange,
}, ref) {
  const { s } = useI18n()
  const [text, setText] = useState('')
  const [uploading, setUploading] = useState<UploadingState | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  useImperativeHandle(ref, () => ({
    fill: (newText: string) => {
      setText(newText)
      setTimeout(() => textareaRef.current?.focus(), 0)
    },
  }), [])
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadAbortRef = useRef<AbortController | null>(null)
  // Set to true by the cancel button to short-circuit the serial-upload
  // loop without throwing through the natural promise chain.
  const cancelledRef = useRef(false)

  // Always-fresh view of `attachedDocs.length` for the upload loop —
  // the loop is one giant `await`, so the captured prop is stale by the
  // time the second iteration runs.  A ref dodges that race.
  const attachedCountRef = useRef(attachedDocs.length)
  attachedCountRef.current = attachedDocs.length

  // Auto-grow textarea up to ~8 lines.
  useEffect(() => {
    const ta = textareaRef.current
    if (!ta) return
    ta.style.height = 'auto'
    ta.style.height = `${Math.min(ta.scrollHeight, 200)}px`
  }, [text])

  // Abort any in-flight upload when the component unmounts (e.g. user
  // hits "New chat" while a 15 MB PDF is still uploading).
  useEffect(() => {
    return () => {
      cancelledRef.current = true
      uploadAbortRef.current?.abort()
    }
  }, [])

  const atCapacity = attachedDocs.length >= MAX_ATTACHMENTS
  const canPick = !isStreaming && !uploading && !atCapacity

  const submit = () => {
    const q = text.trim()
    if (!q || isStreaming || disabled || uploading) return
    onSend(q)
    setText('')
  }

  const onKeyDown = (e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
    // Enter to send, Shift+Enter for newline.
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      submit()
    }
  }

  /**
   * Validate one File client-side.  Returns null if OK, else a short
   * human-readable rejection reason (server still re-validates).
   */
  const validateFile = (file: File): string | null => {
    const lower = file.name.toLowerCase()
    if (!ACCEPT_EXTENSIONS.some((ext) => lower.endsWith(ext))) {
      return `${file.name} — ${s.attachmentSupportedFormats}`
    }
    if (file.size > MAX_FILE_BYTES) {
      return `${file.name} — > 20 MB`
    }
    return null
  }

  /**
   * Upload a batch of picked / dropped files **sequentially**.
   *
   * Sequential (not parallel) keeps memory bounded, makes per-file
   * abort meaningful, and gives the user readable "N of M" progress.
   * One failure does NOT abort the batch — successfully-uploaded files
   * stay attached; the last failure is surfaced via the error chip.
   * User-driven cancel (× on the uploading chip) does abort the batch.
   */
  const processBatch = async (files: File[]) => {
    if (files.length === 0) return

    // 1. Enforce the global cap up front.  Take only as many as fit.
    const slots = MAX_ATTACHMENTS - attachedCountRef.current
    if (slots <= 0) {
      setUploadError(s.attachmentMaxReached(MAX_ATTACHMENTS))
      return
    }
    let rejectedByCap = 0
    if (files.length > slots) {
      rejectedByCap = files.length - slots
      files = files.slice(0, slots)
    }

    // 2. Client-side validate.  Invalid files become rejection messages
    //    bubbled to the user; we still proceed with whatever validated.
    const valid: File[] = []
    const rejections: string[] = []
    for (const f of files) {
      const why = validateFile(f)
      if (why) rejections.push(why)
      else valid.push(f)
    }
    if (rejectedByCap > 0) {
      rejections.unshift(s.attachmentMaxReached(MAX_ATTACHMENTS))
    }

    // Clear the previous error before the batch starts so a successful
    // run leaves no stale red chip behind.
    setUploadError(rejections[0] ?? null)

    if (valid.length === 0) return

    // 3. Serial upload.
    cancelledRef.current = false
    for (let i = 0; i < valid.length; i++) {
      if (cancelledRef.current) break

      const file = valid[i]
      setUploading({
        filename: file.name,
        size_bytes: file.size,
        index: i + 1,
        total: valid.length,
      })

      const ctrl = new AbortController()
      uploadAbortRef.current = ctrl

      try {
        const doc = await uploadDocument(file, ctrl.signal)
        // If the server detected a byte-identical upload it reused the
        // existing doc_id (``doc.deduped === true``).  Two cases:
        //   1. That doc_id is *already* attached — just flag the
        //      existing chip as deduped so it shows the badge; don't
        //      double-add or it'd render two chips for the same file.
        //   2. The user previously removed that chip and is now
        //      re-attaching — append, with the deduped flag preserved.
        onAttachmentsChange((prev) => {
          const existingIndex = prev.findIndex((d) => d.doc_id === doc.doc_id)
          if (existingIndex >= 0) {
            const next = prev.slice()
            next[existingIndex] = { ...next[existingIndex], deduped: true }
            return next
          }
          return [...prev, doc]
        })
      } catch (e: unknown) {
        const isAbort = e instanceof DOMException && e.name === 'AbortError'
        if (isAbort) {
          // Cancel button was hit — stop the entire batch.
          break
        }
        // Soft failure: surface the most recent error, continue batch.
        setUploadError(
          `${file.name}: ${e instanceof Error ? e.message : String(e)}`,
        )
      } finally {
        if (uploadAbortRef.current === ctrl) uploadAbortRef.current = null
      }
    }

    setUploading(null)
    cancelledRef.current = false
  }

  const onPickClick = () => {
    if (!canPick) return
    fileInputRef.current?.click()
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    // Reset so picking the same filename twice still fires onChange.
    e.target.value = ''
    if (files.length) processBatch(files)
  }

  /** Remove one already-attached doc by its server-side ID.
   *
   *  This is a *UI-only* detach: the server keeps the upload around so the
   *  user can re-attach it later (or a future "Recent uploads" panel can
   *  surface it).  The 14-day TTL GC will reclaim the disk if it's never
   *  touched again.  Eagerly DELETE-ing here used to cause data loss when
   *  the user just wanted to swap which docs were attached to the next
   *  question. */
  const removeAttachment = (doc_id: string) => {
    onAttachmentsChange((prev) => prev.filter((d) => d.doc_id !== doc_id))
  }

  /** × on the in-flight upload chip: abort current + skip the rest of the batch. */
  const cancelUpload = () => {
    cancelledRef.current = true
    uploadAbortRef.current?.abort()
    uploadAbortRef.current = null
    setUploading(null)
  }

  // ----- Drag-drop -----
  // We only accept drags that actually carry files (avoid showing the
  // overlay when the user drags selected text from elsewhere).
  const dragHasFile = (e: ReactDragEvent) =>
    Array.from(e.dataTransfer.items || []).some((it) => it.kind === 'file')

  const onDragOver = (e: ReactDragEvent) => {
    if (!dragHasFile(e) || !canPick) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setDragOver(true)
  }
  const onDragLeave = (e: ReactDragEvent) => {
    // Only clear when the drag actually leaves the wrapper, not when it
    // crosses into a child element (relatedTarget is the entered node).
    if (e.currentTarget.contains(e.relatedTarget as Node)) return
    setDragOver(false)
  }
  const onDrop = (e: ReactDragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!canPick) return
    const files = e.dataTransfer.files ? Array.from(e.dataTransfer.files) : []
    if (files.length) processBatch(files)
  }

  const showChipRow =
    attachedDocs.length > 0 || uploading !== null || uploadError !== null
  const submitDisabled = !text.trim() || disabled || uploading !== null

  return (
    <div
      className="border-t border-brand-200 bg-white relative"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* Drop-zone overlay — shown only while a file is being dragged over. */}
      {dragOver && (
        <div className="absolute inset-0 z-10 pointer-events-none border-2 border-dashed border-brand-500 bg-brand-50/90 flex items-center justify-center">
          <div className="text-center">
            <Paperclip className="w-8 h-8 mx-auto mb-2 text-brand-600" />
            <p className="text-sm font-medium text-brand-800">
              {s.attachmentDropHere}
            </p>
            <p className="text-[11px] text-brand-500 mt-1">
              {s.attachmentSupportedFormats}
            </p>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 pt-3 pb-3 flex flex-col gap-2">
        {showChipRow && (
          <div className="flex flex-col gap-1.5">
            {attachedDocs.map((doc) => (
              <AttachmentChip
                key={doc.doc_id}
                doc={doc}
                onRemove={() => removeAttachment(doc.doc_id)}
                disabled={isStreaming}
              />
            ))}
            {uploading && (
              <AttachmentUploadingChip
                filename={uploading.filename}
                size_bytes={uploading.size_bytes}
                index={uploading.index}
                total={uploading.total}
                onCancel={cancelUpload}
              />
            )}
            {uploadError && (
              <AttachmentErrorChip
                error={uploadError}
                onDismiss={() => setUploadError(null)}
              />
            )}
          </div>
        )}

        <div className="flex gap-2 items-end">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={ACCEPT_ATTR}
            onChange={onFileChange}
            className="hidden"
          />
          <button
            type="button"
            onClick={onPickClick}
            disabled={!canPick}
            className="rounded-lg border border-brand-200 hover:border-brand-400 hover:bg-brand-50 text-brand-600 hover:text-brand-800 p-2 flex-shrink-0 transition-colors disabled:text-brand-300 disabled:hover:bg-white disabled:hover:border-brand-200 disabled:cursor-not-allowed"
            aria-label={s.attachmentAttach}
            title={
              atCapacity
                ? s.attachmentMaxReached(MAX_ATTACHMENTS)
                : s.attachmentAttachTitle
            }
          >
            <Paperclip className="w-5 h-5" />
          </button>

          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={onKeyDown}
            rows={1}
            placeholder={
              isStreaming ? s.composerPlaceholderStreaming : s.composerPlaceholder
            }
            disabled={disabled}
            className="flex-1 resize-none rounded-lg border border-brand-200 px-3 py-2 text-sm leading-relaxed focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-brand-50 disabled:text-brand-400"
            style={{ maxHeight: 200 }}
          />

          {isStreaming ? (
            <button
              type="button"
              onClick={onStop}
              className="rounded-lg bg-red-600 hover:bg-red-700 text-white p-2 flex-shrink-0 transition-colors"
              aria-label={s.stopTitle}
              title={s.stopTitle}
            >
              <StopCircle className="w-5 h-5" />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitDisabled}
              className="rounded-lg bg-brand-700 hover:bg-brand-800 text-white p-2 flex-shrink-0 transition-colors disabled:bg-brand-300 disabled:cursor-not-allowed"
              aria-label={s.send}
              title={s.sendTitle}
            >
              <Send className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-2 text-[11px] text-brand-400 flex justify-between gap-4">
        <span className="truncate">
          {attachedDocs.length > 0
            ? s.attachmentActiveHint(
                attachedDocs.length,
                attachedDocs[0].filename,
              )
            : s.multiTurnHint}
        </span>
        <span className="flex-shrink-0">{s.enterHint}</span>
      </div>
    </div>
  )
})
